import { useInfiniteQuery, type InfiniteData } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';
import moment from 'moment';
import {
  fetchSlackConversation,
  SlackConversationResponse,
  FetchSlackConversationParams,
} from '../../../api/slackConversation';

const DAYS_INITIAL_BEFORE = 1;
const DAYS_INITIAL_AFTER = 1;
const DAYS_PAGE_BEFORE = 2;
const DAYS_PAGE_AFTER = 2;

function formatDateForApi(date: string): string {
  return moment(date).format('YYYY-MM-DD');
}

function addDays(dateStr: string, days: number): string {
  return formatDateForApi(moment(dateStr).add(days, 'days').toISOString());
}

function subtractDays(dateStr: string, days: number): string {
  return formatDateForApi(moment(dateStr).subtract(days, 'days').toISOString());
}

export interface UseSlackConversationParams {
  channelId: string;
  centerDate: string;
  cutMode?: 'day' | 'thread';
  enabled: boolean;
}

export interface SlackConversationPageParam {
  startDate: string;
  endDate: string;
}

export function useSlackConversation({
  channelId,
  centerDate,
  cutMode = 'day',
  enabled,
}: UseSlackConversationParams) {
  const { getAccessTokenSilently } = useAuth0();
  const auth0 = { getAccessTokenSilently };

  return useInfiniteQuery<
    SlackConversationResponse,
    Error,
    InfiniteData<SlackConversationResponse>,
    (string | undefined)[],
    SlackConversationPageParam
      >({
        queryKey: ['slackConversation', channelId, centerDate, cutMode],
        queryFn: async ({ pageParam }: { pageParam: SlackConversationPageParam }) => {
          const params: FetchSlackConversationParams = {
            channelId,
            startDate: pageParam.startDate,
            endDate: pageParam.endDate,
            cutMode,
          };
          return fetchSlackConversation(params, auth0);
        },
        initialPageParam: {
          startDate: subtractDays(centerDate, DAYS_INITIAL_BEFORE),
          endDate: addDays(centerDate, DAYS_INITIAL_AFTER),
        },
        getNextPageParam: (lastPage): SlackConversationPageParam | undefined => {
          if (!lastPage.hasMoreAfter || !lastPage.latestDate) return undefined;
          return {
            startDate: addDays(lastPage.latestDate, 1),
            endDate: addDays(lastPage.latestDate, DAYS_PAGE_AFTER),
          };
        },
        getPreviousPageParam: (firstPage): SlackConversationPageParam | undefined => {
          if (!firstPage.hasMoreBefore || !firstPage.earliestDate) return undefined;
          return {
            startDate: subtractDays(firstPage.earliestDate, DAYS_PAGE_BEFORE),
            endDate: subtractDays(firstPage.earliestDate, 1),
          };
        },
        enabled: enabled && !!channelId && !!centerDate,
        staleTime: 2 * 60 * 1000,
      });
}

export interface SlackConversationDayFlat {
  date: string;
  messages: any[];
  channelName: string;
  participants: any[];
}

export function useFlattenedSlackDays(
  data: { pages?: Array<{ days: SlackConversationDayFlat[] }> } | undefined
): SlackConversationDayFlat[] {
  if (!data?.pages?.length) return [];
  const allDays = data.pages.flatMap((p: { days: SlackConversationDayFlat[] }) => p.days);
  const byDate = new Map<string, SlackConversationDayFlat>();
  allDays.forEach((day: SlackConversationDayFlat) => byDate.set(day.date, day));
  return Array.from(byDate.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, day]) => day);
}
